import React, { useEffect, useState } from "react";
import {
    IconButton,
    Badge,
    Menu,
    MenuItem,
    Typography,
    Box,
    Button,
    Divider,
} from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import axios from "axios";
import { getValidAccessToken, handleLogout } from "../utils/tokenUtils";
import { useNavigate } from "react-router-dom";
import { useLoader } from "../context/LoaderContext";

const apiUrl = process.env.REACT_APP_API_URL;

const NotificationIconWithDropdown = () => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [notifications, setNotifications] = useState([]);

    const { showLoader, hideLoader } = useLoader();
    const navigate = useNavigate();

    const open = Boolean(anchorEl);

    const handleOpen = (event) => setAnchorEl(event.currentTarget);
    const handleClose = () => setAnchorEl(null);

    const fetchMessages = async () => {
        const token = await getValidAccessToken();
        if (!token) {
            handleLogout();
            return [];
        }
        try {
            showLoader();
            const unreadRes = await axios.get(
                `${apiUrl}/notification-messages/?message_status=UnRead`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            // const readRes = await axios.get(
            //     `${apiUrl}/notification-messages/?message_status=Read`,
            //     { headers: { Authorization: `Bearer ${token}` } }
            // );

            // Merge: unread should appear first
            return [
                ...unreadRes.data.data.map((m) => ({ ...m, isRead: false })),
                // ...readRes.data.data.map((m) => ({ ...m, isRead: true })),
            ];
        } catch (error) {
            console.error("Error fetching messages:", error);
            return [];
        } finally {
            hideLoader();
        }
    };

    useEffect(() => {
        const load = async () => {
            const all = await fetchMessages();
            setNotifications(all);
        };
        load();
    }, []);

    const handleMarkMessage = async (id, newStatus) => {
        const token = await getValidAccessToken();
        if (!token) return handleLogout();

        try {
            await axios.post(
                `${apiUrl}/notifications/mark-read-unread/`,
                {
                    notification_id: id,
                    message_status: newStatus,
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            const all = await fetchMessages();
            setNotifications(all);
        } catch (err) {
            console.error("Error updating message status:", err);
        }
    };

    const handleTicketClick = (ticketId, notificationId, isRead) => {
        if (!isRead) handleMarkMessage(notificationId, "Read");
        navigate(`/ticket-data?ticket_id=${ticketId}`);
        handleClose();
    };

    const unreadCount = notifications.filter((n) => !n.isRead).length;

    return (
        <>
            {/* Notification Bell */}
            <IconButton color="inherit" onClick={handleOpen}>
                <Badge color="error" badgeContent={unreadCount}>
                    <NotificationsIcon />
                </Badge>
            </IconButton>

            {/* Dropdown Menu */}
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                PaperProps={{
                    sx: { width: 360, maxHeight: 450, overflowY: "auto", paddingY: 1 },
                }}
            >
                <Typography sx={{ px: 2, pb: 1, fontWeight: 600 }}>
                    Notifications
                </Typography>
                <Divider />

                {notifications.length === 0 && (
                    <Typography sx={{ p: 2, textAlign: "center" }}>
                        No notifications available
                    </Typography>
                )}

                {notifications.map((msg) => (
                    <MenuItem
                        key={msg.notification_id}
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-start",
                            bgcolor: msg.isRead ? "white" : "#f0f7ff",
                            mb: 1,
                            borderRadius: "6px",
                            py: 1.5,
                            px: 1.5,
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                width: "100%",
                                alignItems: "center",
                            }}
                        >
                            {/* Blue Dot Indicator for Unread */}
                            {msg.message_status === "UnRead" && (
                                <FiberManualRecordIcon
                                    sx={{ color: "#1976d2", fontSize: "10px", mr: 1 }}
                                />
                            )}

                            <Typography
                                sx={{
                                    fontSize: "0.85rem",
                                    fontWeight: 600,
                                    cursor: "pointer",
                                    textDecoration: "underline",
                                }}
                                onClick={() =>
                                    handleTicketClick(
                                        msg.ticket_id,
                                        msg.notification_id,
                                        msg.message_status === "UnRead"? false : true
                                    )
                                }
                            >
                                Ticket #{msg.ticket_id}
                            </Typography>
                        </Box>

                        <Typography sx={{ fontSize: "0.75rem", mt: 0.5 }}>
                            From:{" "}
                            {msg.sender_type === "user"
                                ? msg.username
                                : msg.agent_name || "N/A"}
                        </Typography>

                        <Typography sx={{ fontSize: "0.8rem", mt: 0.5 }}>
                            {msg.message_content}
                        </Typography>

                        <Typography sx={{ fontSize: "0.7rem", mt: 0.5, opacity: 0.7 }}>
                            {new Date(msg.timestamp).toLocaleString()}
                        </Typography>

                        {/* Mark read/unread */}
                        <Button
                            variant="text"
                            size="small"
                            sx={{ fontSize: "0.7rem", mt: 1, alignSelf: "flex-end" }}
                            onClick={() =>
                                handleMarkMessage(
                                    msg.notification_id,
                                    msg.isRead ? "UnRead" : "Read"
                                )
                            }
                        >
                            {msg.isRead ? "Mark Unread" : "Mark Read"}
                        </Button>
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
};

export default NotificationIconWithDropdown;
